import * as React from "react";
import { getLocalizedText } from "../util";
import { LazyLoadImage } from "react-lazy-load-image-component";
import Listup from "./listup";
import { newsData } from "../data";
import images from "url:../../images/*.jpg";
import gifs from "url:../../images/*.gif";
import videos from "url:../../videos/*.mp4";
import { getAssetUrl, mergeAssetMaps, resolveAssetMap } from "../asset";
import { useLang } from "../lang";

const News: React.FC = () => {
  const { langEn } = useLang();
  const imageMap = mergeAssetMaps(resolveAssetMap(images), resolveAssetMap(gifs));
  const videoMap = resolveAssetMap(videos);
  const getImageUrl = (imageKey?: string) =>
    imageKey?.startsWith("http") ? imageKey : getAssetUrl(imageMap, imageKey);
  const renderImage = (imageKey?: string, alt?: string) => {
    const imageUrl = getImageUrl(imageKey);
    return imageUrl ? (
      <LazyLoadImage
        src={imageUrl}
        alt={alt ?? ""}
        effect="opacity"
        className="bd-placeholder-img img-fluid w-100"
        style={{ aspectRatio: "16 / 9", objectFit: "cover" }}
      />
    ) : (
      ""
    );
  };
  const renderVideo = (videoKey?: string) => {
    const videoUrl = getAssetUrl(videoMap, videoKey);
    return videoUrl ? (
      <video
        src={videoUrl}
        autoPlay
        loop
        muted
        playsInline
        className="bd-placeholder-img img-fluid w-100"
        style={{ aspectRatio: "16 / 9", objectFit: "cover" }}
      />
    ) : (
      ""
    );
  };
  const renderHighlightCards = (items: typeof newsData.highlights) =>
    items.map((item, i) => (
      <div className="card mb-2 paper" key={i}>
        {item.bgImage ? (
          <div
            className="card-body pt-2 pb-1"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.42), rgba(0, 0, 0, 0.42)), url(${getImageUrl(
                item.bgImage
              )})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: "0.25rem",
              minHeight: "280px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              color: "#fff",
            }}
          >
            <p className="card-title mb-1 h6">{getLocalizedText(item.title, langEn)}</p>
            <p className="card-text mb-1 small">{getLocalizedText(item.description, langEn)}</p>
            <p className="card-text mb-1">
              {item.projectPage ? (
                <a
                  className="academic-icon fas fa-home"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="project page"
                  href={item.projectPage}
                  style={{ color: "#fff" }}
                ></a>
              ) : (
                ""
              )}
              {item.paperUrl ? (
                <a
                  className="academic-icon fas fa-sticky-note"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="paper"
                  href={item.paperUrl}
                  style={{ color: "#fff" }}
                ></a>
              ) : (
                ""
              )}
            </p>
          </div>
        ) : (
          <div className="row no-gutters">
            <div className="col-md-3">
              {item.video
                ? renderVideo(item.video)
                : renderImage(item.img, getLocalizedText(item.title, langEn))}
            </div>

            <div className="col-md-9">
              <div className="card-body pt-2 pb-1">
                <p className="card-title mb-1 h6">{getLocalizedText(item.title, langEn)}</p>
                <p className="card-text mb-1 small">{getLocalizedText(item.description, langEn)}</p>
                <p className="card-text mb-1">
                  {item.projectPage ? (
                    <a
                      className="academic-icon fas fa-home"
                      data-toggle="tooltip"
                      data-placement="top"
                      title="project page"
                      href={item.projectPage}
                    ></a>
                  ) : (
                    ""
                  )}
                  {item.paperUrl ? (
                    <a
                      className="academic-icon fas fa-sticky-note"
                      data-toggle="tooltip"
                      data-placement="top"
                      title="paper"
                      href={item.paperUrl}
                    ></a>
                  ) : (
                    ""
                  )}
                  {item.youtube ? (
                    <a
                      className="academic-icon fab fa-youtube"
                      data-toggle="tooltip"
                      data-placement="top"
                      title="YouTube"
                      href={item.youtube}
                    ></a>
                  ) : (
                    ""
                  )}
                  {item.speakerdeck ? (
                    <a
                      className="academic-icon fab fa-speaker-deck"
                      data-toggle="tooltip"
                      data-placement="top"
                      title="slide"
                      href={item.speakerdeck}
                    ></a>
                  ) : (
                    ""
                  )}
                  {item.recording ? (
                    <a
                      className="academic-icon fas fa-video"
                      data-toggle="tooltip"
                      data-placement="top"
                      title="recording"
                      href={item.recording}
                    ></a>
                  ) : (
                    ""
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    ));

  return (
    <div id="news" className="container justify-content-center">
      <p className="title">{langEn ? "News" : "ニュース"}</p>
      <div className="row text-left">
        <div className="col-md-12 mb-2 mt-2">
          <p className="sub-title">{langEn ? "Highlights" : "ハイライト"}</p>
          {renderHighlightCards(newsData.highlights)}
          {newsData.introTalks && newsData.introTalks.length > 0 ? (
            <>
              <p className="sub-title mt-3">{langEn ? "Featured Talks" : "Featured Talks"}</p>
              {renderHighlightCards(newsData.introTalks)}
            </>
          ) : (
            ""
          )}
        </div>

        {newsData.recruit && newsData.recruit.length > 0 ? (
          <Listup
            items={newsData.recruit}
            title_en="Recruitment, Advertisement"
            title_ja="募集・宣伝"
          />
        ) : (
          ""
        )}

        {newsData.future && newsData.future.length > 0 ? (
          <Listup items={newsData.future} title_en="Upcoming Events" title_ja="今後のイベント" />
        ) : (
          ""
        )}

        <Listup
          items={newsData.activities}
          item_nums={5}
          title_en="Research Activities"
          title_ja="研究活動"
        />
      </div>
    </div>
  );
};

export default News;
